import React, { useState, useRef, useEffect } from 'react';
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
  isAfter,
  isBefore,
} from 'date-fns';

export default function DatePicker({ value, onChange, max, min }) {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(value ? parseISO(value) : new Date());
  const ref = useRef(null);

  const selected = value ? parseISO(value) : null;
  const maxDate = max ? parseISO(max) : null;
  const minDate = min ? parseISO(min) : null;

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const calStart = startOfWeek(monthStart);
  const calEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: calStart, end: calEnd });

  const isDisabled = (day) => {
    if (maxDate && isAfter(day, maxDate)) return true;
    if (minDate && isBefore(day, minDate)) return true;
    return false;
  };

  const canGoNext = !maxDate || isBefore(endOfMonth(addMonths(viewMonth, 0)), maxDate);

  const handleSelect = (day) => {
    if (isDisabled(day)) return;
    onChange(format(day, 'yyyy-MM-dd'));
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between rounded-lg bg-white/10 border border-white/20 text-white shadow-sm px-3 py-2 text-left hover:bg-white/15 transition"
      >
        <span>{selected ? format(selected, 'MMM d, yyyy') : 'Select date'}</span>
        <svg className="w-4 h-4 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-2 left-0 w-72 glass rounded-xl p-4 shadow-2xl border border-white/20">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setViewMonth(subMonths(viewMonth, 1))}
              className="p-1 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-semibold text-white">
              {format(viewMonth, 'MMMM yyyy')}
            </span>
            <button
              type="button"
              onClick={() => canGoNext && setViewMonth(addMonths(viewMonth, 1))}
              disabled={!canGoNext}
              className="p-1 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
              <div key={d} className="text-center text-xs font-medium text-white/40 py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map(day => {
              const inMonth = isSameMonth(day, viewMonth);
              const isSelected = selected && isSameDay(day, selected);
              const isToday = isSameDay(day, new Date());
              const disabled = isDisabled(day);

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => handleSelect(day)}
                  disabled={disabled}
                  className={`
                    aspect-square rounded-lg text-sm font-medium transition flex items-center justify-center
                    ${isSelected
                      ? 'bg-white text-black'
                      : isToday
                        ? 'bg-white/20 text-white'
                        : inMonth
                          ? 'text-white/80 hover:bg-white/10'
                          : 'text-white/20'}
                    ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>

          {/* Today shortcut */}
          <div className="mt-3 pt-3 border-t border-white/10 flex justify-end">
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                setViewMonth(today);
                handleSelect(today);
              }}
              className="text-xs text-white/60 hover:text-white transition px-2 py-1 rounded hover:bg-white/10"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
